import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { CheckCircle2, ClipboardList, Eye, Trash2, Filter, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/utils/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FILTERS = { search: "", rm: "all", shiftingDateFrom: "", shiftingDateTo: "", sortBy: "created_at" };
const phoneDigits = value => String(value || "").replace(/\D/g, "");
const whatsappNumber = value => {
  const digits = phoneDigits(value);
  return digits.length === 10 ? `91${digits}` : digits;
};

const PhoneActions = ({ booking, stopRow = false }) => {
  const digits = phoneDigits(booking.phone);
  return <div className="mt-1 flex items-center gap-1.5">
    <span className="text-xs font-bold text-[#334155]">{booking.phone || "—"}</span>
    {digits && <>
      <a href={`tel:${digits}`} onClick={event => { if (stopRow) event.stopPropagation(); }} aria-label={`Call ${booking.customer_name}`} title="Call" className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 text-blue-700 hover:bg-blue-700 hover:text-white"><Phone size={12} /></a>
      <a href={`https://wa.me/${whatsappNumber(booking.phone)}`} target="_blank" rel="noreferrer" onClick={event => { if (stopRow) event.stopPropagation(); }} aria-label={`WhatsApp ${booking.customer_name}`} title="WhatsApp" className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-50 text-green-700 hover:bg-green-700 hover:text-white"><MessageCircle size={12} /></a>
    </>}
  </div>;
};

const formatINR = (v) =>
  v != null && v !== ""
    ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(v)
    : "—";

const safeDate = (val) => {
  if (!val) return "—";
  try { const d = new Date(val); return isNaN(d.getTime()) ? "—" : format(d, "dd MMM yyyy"); } catch { return "—"; }
};

const normalizedShiftingDate = value => {
  const raw = String(value || "");
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const legacy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return legacy ? `${legacy[3]}-${legacy[2]}-${legacy[1]}` : "";
};

const shiftingDateStyle = value => {
  const normalized = normalizedShiftingDate(value);
  if (!normalized) return "text-[#475569]";
  const today = format(new Date(), "yyyy-MM-dd");
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrow = format(tomorrowDate, "yyyy-MM-dd");
  if (normalized < today) return "font-bold text-red-600";
  if (normalized <= tomorrow) return "font-bold text-orange-600";
  return "font-bold text-green-700";
};

const displayShiftingDate = value => {
  const normalized = normalizedShiftingDate(value);
  return normalized ? safeDate(`${normalized}T00:00:00`) : "—";
};

const STATUS_STYLE = {
  Active:    "bg-green-50 text-green-700 border-green-200",
  Completed: "bg-blue-50 text-blue-700 border-blue-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
};

export default function BookingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();
  const isAdmin = ["admin", "superadmin", "manager"].includes(authUser?.role);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [relationshipManagers, setRelationshipManagers] = useState([]);
  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [activeFilters, setActiveFilters] = useState(EMPTY_FILTERS);
  const loadingRef = useRef(false);
  const loadMoreRef = useRef(null);
  const showCompleted = location.pathname.endsWith("/completed");
  const visibleBookings = bookings;

  const fetchBookings = async (requestedPage = 1, append = false, filters = activeFilters) => {
    if (append && loadingRef.current) return;
    if (append) { loadingRef.current = true; setLoadingMore(true); }
    try {
      const { data } = await axios.get(`${API}/confirmed-leads/paginated`, {
        params: {
          page: requestedPage,
          limit: 50,
          completed: showCompleted,
          rm: showCompleted ? "all" : filters.rm,
          shifting_date_from: showCompleted ? "" : filters.shiftingDateFrom,
          shifting_date_to: showCompleted ? "" : filters.shiftingDateTo,
          sort_by: showCompleted ? "created_at" : filters.sortBy,
          search: filters.search,
        },
        withCredentials: true,
      });
      setBookings(previous => append ? [...previous, ...data.items] : data.items);
      setPage(data.page); setTotal(data.total); setHasMore(data.has_more);
    } catch { toast.error("Failed to load confirmed leads."); }
    finally { setLoading(false); if (append) { loadingRef.current = false; setLoadingMore(false); } }
  };

  useEffect(() => {
    const cleared = { ...EMPTY_FILTERS };
    setFilterDraft(cleared);
    setActiveFilters(cleared);
    setLoading(true);
    fetchBookings(1, false, cleared);
  }, [showCompleted]);
  useEffect(() => {
    axios.get(`${API}/users/relationship-managers`, { withCredentials: true })
      .then(response => setRelationshipManagers(response.data))
      .catch(() => {});
  }, []);
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && !loadingRef.current) fetchBookings(page + 1, true);
    }, { rootMargin: "250px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, page, showCompleted]);

  const applyShiftingSort = () => {
    const nextSort = activeFilters.sortBy === "shifting_date" ? "created_at" : "shifting_date";
    const nextFilters = { ...activeFilters, sortBy: nextSort };
    setFilterDraft(current => ({ ...current, sortBy: nextSort }));
    setActiveFilters(nextFilters);
    setLoading(true);
    fetchBookings(1, false, nextFilters);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/confirmed-leads/${id}`, { withCredentials: true });
      setBookings(prev => prev.filter(b => b.id !== id));
      setTotal(value => Math.max(0, value - 1));
      toast.success("Confirmed lead deleted.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to delete."));
    }
  };

  const handleComplete = async (booking) => {
    if (!window.confirm(`Mark “${booking.customer_name}” as completed?`)) return;
    setStatusUpdating(booking.id);
    try {
      const { data } = await axios.patch(
        `${API}/confirmed-leads/${booking.id}`,
        { status: "Completed" },
        { withCredentials: true }
      );
      setBookings(previous => previous.filter(item => item.id !== booking.id));
      setTotal(value => Math.max(0, value - 1));
      toast.success("Confirmed lead marked as completed.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to complete confirmed lead."));
    } finally {
      setStatusUpdating(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="bookings-page">

      <div>
        <h2 className="text-xl font-bold text-[#1B2A6B]">{showCompleted ? "Completed" : "Active"} Confirmed Leads</h2>
        <p className="text-xs text-[#475569] mt-0.5">
          {total} confirmed lead{total !== 1 ? "s" : ""} · {visibleBookings.length} loaded
        </p>
      </div>

      <div className="border border-[#E2E8F0] bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-[#1B2A6B]">Customer Name or Number</label>
            <input
              type="search"
              value={filterDraft.search}
              onChange={event => setFilterDraft(current => ({ ...current, search: event.target.value }))}
              onKeyDown={event => {
                if (event.key !== "Enter") return;
                const nextFilters = { ...filterDraft };
                setActiveFilters(nextFilters);
                setLoading(true);
                fetchBookings(1, false, nextFilters);
              }}
              placeholder="Search customer"
              className="h-10 w-full border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A] focus:border-[#1B2A6B] focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              const nextFilters = { ...filterDraft };
              setActiveFilters(nextFilters);
              setLoading(true);
              fetchBookings(1, false, nextFilters);
            }}
            className="inline-flex h-10 items-center justify-center border-b-2 border-[#FFD700] bg-[#1B2A6B] px-5 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#152257]"
          >
            <Filter size={13} className="mr-1.5" />Search
          </button>
        </div>
      </div>

      {!showCompleted && <div className="border border-[#E2E8F0] bg-white p-4" data-testid="confirmed-lead-filters">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(240px,1fr)_minmax(360px,1.5fr)_auto] sm:items-start">
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-[#1B2A6B]">Assigned RM</label>
            <select
              value={filterDraft.rm}
              onChange={event => setFilterDraft(current => ({ ...current, rm: event.target.value }))}
              className="h-10 w-full border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A] focus:border-[#1B2A6B] focus:outline-none"
            >
              <option value="all">All</option>
              <option value="unassigned">Unassigned</option>
              {relationshipManagers.map(rm => <option key={rm.id} value={`id:${rm.id}`}>{rm.name}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-[#1B2A6B]">Shifting Date Range</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-[#64748B]">From</span>
                <input
                  type="date"
                  value={filterDraft.shiftingDateFrom}
                  onChange={event => setFilterDraft(current => ({ ...current, shiftingDateFrom: event.target.value }))}
                  className="h-10 w-full border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A] focus:border-[#1B2A6B] focus:outline-none"
                />
              </div>
              <div>
                <span className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-[#64748B]">To</span>
                <input
                  type="date"
                  value={filterDraft.shiftingDateTo}
                  min={filterDraft.shiftingDateFrom || undefined}
                  onChange={event => setFilterDraft(current => ({ ...current, shiftingDateTo: event.target.value }))}
                  className="h-10 w-full border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A] focus:border-[#1B2A6B] focus:outline-none"
                />
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={filterDraft.sortBy === "shifting_date"}
              onClick={applyShiftingSort}
              className="mt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-[#475569]"
              data-testid="sort-confirmed-by-shifting-date"
            >
              Sort By
              <span className={`relative h-4 w-7 rounded-full transition-colors ${filterDraft.sortBy === "shifting_date" ? "bg-[#1B2A6B]" : "bg-[#CBD5E1]"}`}>
                <span className={`absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white transition-transform ${filterDraft.sortBy === "shifting_date" ? "translate-x-3" : "translate-x-0"}`} />
              </span>
            </button>
          </div>
          <button
            type="button"
            onClick={() => {
              if (filterDraft.shiftingDateFrom && filterDraft.shiftingDateTo && filterDraft.shiftingDateFrom > filterDraft.shiftingDateTo) {
                toast.error("Shifting Date From cannot be after To.");
                return;
              }
              const nextFilters = { ...filterDraft };
              setActiveFilters(nextFilters);
              setLoading(true);
              fetchBookings(1, false, nextFilters);
            }}
            className="mt-[39px] inline-flex h-10 items-center justify-center border-b-2 border-[#FFD700] bg-[#1B2A6B] px-5 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#152257]"
          >
            <Filter size={13} className="mr-1.5" />Filter
          </button>
        </div>
      </div>}

      <div className="bg-white border border-[#E2E8F0]">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-[#94A3B8]">Loading...</div>
        ) : visibleBookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <ClipboardList size={40} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">No {showCompleted ? "completed" : "active"} confirmed leads.</p>
          </div>
        ) : !showCompleted ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1450px] text-sm" data-testid="bookings-table">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                  {["Name & Number", "Assigned RM", "Shifting Date", "Moving From", "Moving To", "Total Amount", "Token Amount Received", "Amount at Pick Up", "Status"].map(header => (
                    <th key={header} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleBookings.map((booking, index) => {
                  const pickupAmount = Math.max(0, Number(booking.quotation || 0) - Number(booking.advance_paid || 0));
                  return (
                    <tr
                      key={booking.id}
                      data-testid={`booking-row-${index}`}
                      tabIndex={0}
                      role="link"
                      onClick={() => navigate(`/confirmed-leads/${booking.id}`)}
                      onKeyDown={event => { if (event.key === "Enter" || event.key === " ") navigate(`/confirmed-leads/${booking.id}`); }}
                      className={`border-b border-[#E2E8F0] cursor-pointer hover:bg-[#EEF2FF] focus:bg-[#EEF2FF] focus:outline-none ${index % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="font-semibold text-[#1B2A6B]">{booking.customer_name}</p>
                        <PhoneActions booking={booking} stopRow />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-semibold text-[#1B2A6B]">{booking.assigned_to_name || "Unassigned"}</td>
                      <td className={`px-4 py-3 whitespace-nowrap text-xs ${shiftingDateStyle(booking.shifting_date)}`}>{displayShiftingDate(booking.shifting_date)}</td>
                      <td className="px-4 py-3 max-w-[230px]"><span className="block truncate text-[#0F172A]" title={booking.moving_from || ""}>{booking.moving_from || "—"}</span></td>
                      <td className="px-4 py-3 max-w-[230px]"><span className="block truncate text-[#0F172A]" title={booking.moving_to || ""}>{booking.moving_to || "—"}</span></td>
                      <td className="px-4 py-3 whitespace-nowrap font-semibold text-[#0F172A]">{formatINR(booking.quotation)}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-semibold text-green-700">{formatINR(booking.advance_paid)}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-semibold text-amber-700">{formatINR(pickupAmount)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_STYLE[booking.status] || STATUS_STYLE.Active}`}>
                            {booking.status}
                          </span>
                          <button
                            type="button"
                            onClick={event => { event.stopPropagation(); handleComplete(booking); }}
                            disabled={statusUpdating === booking.id}
                            className="inline-flex items-center gap-1 border border-green-600 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-green-700 hover:bg-green-700 hover:text-white disabled:opacity-50"
                            data-testid={`complete-booking-${index}`}
                          >
                            <CheckCircle2 size={10} />{statusUpdating === booking.id ? "Saving" : "Complete"}
                          </button>
                          {isAdmin && <button
                            type="button"
                            onClick={event => { event.stopPropagation(); handleDelete(booking.id, booking.customer_name); }}
                            className="p-1 text-[#CBD5E1] hover:text-red-500"
                            title="Delete (Admin only)"
                            data-testid={`delete-booking-${index}`}
                          ><Trash2 size={13} /></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                {["#", "Customer", "Route", "Status", ""].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleBookings.map((b, idx) => {
                const rowBg = idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]";
                return (
                  <React.Fragment key={b.id}>
                    {/* Row 1 — primary */}
                    <tr data-testid={`booking-row-${idx}`} className={rowBg}>
                      <td className="px-4 pt-3 pb-1 text-[#94A3B8] text-xs font-mono w-8">{idx + 1}</td>
                      <td className="px-4 pt-3 pb-1">
                        <button
                          onClick={() => navigate(`/confirmed-leads/${b.id}`)}
                          className="font-semibold text-[#1B2A6B] hover:underline underline-offset-2 text-left"
                          data-testid={`booking-name-${idx}`}
                        >
                          {b.customer_name}
                        </button>
                        <PhoneActions booking={b} />
                      </td>
                      <td className="px-4 pt-3 pb-1">
                        <span className="font-medium text-[#0F172A]">{b.moving_from || "—"}</span>
                        <span className="text-[#94A3B8] mx-1.5">→</span>
                        <span className="font-medium text-[#0F172A]">{b.moving_to || "—"}</span>
                      </td>
                      <td className="px-4 pt-3 pb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_STYLE[b.status] || STATUS_STYLE.Active}`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="px-4 pt-3 pb-1">
                        <div className="flex items-center gap-1.5">
                          <button
                            data-testid={`view-booking-${idx}`}
                            onClick={() => navigate(`/confirmed-leads/${b.id}`)}
                            className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#1B2A6B] hover:text-white border border-[#1B2A6B] hover:bg-[#1B2A6B] transition-colors px-2 py-1 rounded-sm"
                          >
                            <Eye size={11} />View
                          </button>
                          {!showCompleted && (
                            <button
                              type="button"
                              onClick={() => handleComplete(b)}
                              disabled={statusUpdating === b.id}
                              className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-green-700 hover:text-white border border-green-600 hover:bg-green-700 transition-colors px-2 py-1 rounded-sm disabled:opacity-50"
                              data-testid={`complete-booking-${idx}`}
                            >
                              <CheckCircle2 size={11} />{statusUpdating === b.id ? "Saving" : "Complete"}
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              data-testid={`delete-booking-${idx}`}
                              onClick={() => handleDelete(b.id, b.customer_name)}
                              className="text-[#CBD5E1] hover:text-red-500 transition-colors p-1"
                              title="Delete (Admin only)"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Row 2 — secondary details */}
                    <tr className={`border-b border-[#E2E8F0] ${rowBg}`}>
                      <td colSpan={5} className="px-4 pt-0.5 pb-3">
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-0.5 text-xs text-[#64748B]">
                          {b.shifting_date && (
                            <span><span className="font-medium text-[#475569]">Shifting:</span> {safeDate(b.shifting_date + "T00:00:00")}</span>
                          )}
                          {b.loads_count > 0 && (
                            <span>
                              <span className="font-medium text-[#475569]">Loads:</span>{" "}
                              {b.loads_count} item{b.loads_count !== 1 ? "s" : ""}
                            </span>
                          )}
                          {b.quotation > 0 && (
                            <span><span className="font-medium text-[#475569]">Quotation:</span> {formatINR(b.quotation)}</span>
                          )}
                          {b.advance_paid > 0 && (
                            <span>
                              <span className="font-medium text-[#475569]">Advance:</span>{" "}
                              <span className="text-green-700 font-semibold">{formatINR(b.advance_paid)}</span>
                            </span>
                          )}
                          {b.assigned_to_name ? (
                            <span>
                              <span className="font-medium text-[#475569]">RM:</span>{" "}
                              <span className="text-[#1B2A6B] font-semibold">{b.assigned_to_name}</span>
                            </span>
                          ) : (
                            <span className="italic text-[#CBD5E1]">Unassigned</span>
                          )}
                          <span className="text-[#94A3B8]">Booked {safeDate(b.created_at)}</span>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div ref={loadMoreRef} className="flex min-h-10 items-center justify-center text-xs text-[#64748B]">
        {loadingMore ? "Loading 50 more confirmed leads..." : hasMore ? "Scroll to load more" : visibleBookings.length ? "All confirmed leads loaded" : null}
      </div>
    </div>
  );
}
